import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import {
    MessageSquare, Send, X, Users,
    FileText, Loader2, Paperclip, Smile, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatService } from '../services/ChatService';

// Lazy load EmojiPicker
const EmojiPicker = lazy(() => import('emoji-picker-react'));

// Individual Chat Window Component
const ChatWindow = ({
    chat,
    index,
    userEmail,
    onClose,
    onSend,
    onInputChange,
    onFileSelect,
    onRemoveAttachment,
    onEmojiClick,
    onToggleEmojiPicker,
    getOtherParticipantName
}) => {
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chat.messages]);

    // Position from the right: base position + stacked offset
    const rightPosition = 420 + (index * 400); // 420px for conversation list + 400px per window

    return (
        <motion.div
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.95 }}
            style={{ right: `${rightPosition}px` }}
            className="fixed bottom-6 z-[100] dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl shadow-2xl w-[380px] h-[600px] flex flex-col overflow-hidden backdrop-blur-xl"
        >
            {/* Header */}
            <div className="p-3 px-4 border-b dark:border-slate-800 border-slate-200 bg-gradient-to-r dark:from-indigo-900/20 dark:to-yellow-900/20 from-indigo-50 to-yellow-50 backdrop-blur-md flex justify-between items-center shadow-sm z-20">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-yellow-500 flex items-center justify-center shadow-md">
                            <MessageSquare className="text-white" size={20} />
                        </div>
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 dark:border-slate-900 border-white rounded-full"></span>
                    </div>
                    <div>
                        <h3 className="font-bold dark:text-white text-slate-900 leading-snug truncate max-w-[220px]">
                            {getOtherParticipantName(chat.conversation)}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Online
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-slate-400 hover:text-red-500 transition-colors"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-white dark:bg-slate-950">
                {chat.isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                    </div>
                ) : chat.messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <MessageSquare size={32} className="mb-2 opacity-50" />
                        <p className="text-sm">No messages yet</p>
                    </div>
                ) : (
                    chat.messages.map((msg, idx) => {
                        const isMe = msg.sender_email === userEmail;
                        return (
                            <div key={msg.id || idx} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                                {!isMe && (
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 text-xs font-bold text-indigo-600 self-end mb-1">
                                        {(msg.sender_name || msg.sender_email || '?').charAt(0)}
                                    </div>
                                )}
                                <div className={`flex flex-col max-w-[75%] ${isMe ? 'items-end' : 'items-start'} space-y-1`}>
                                    {/* Attachments */}
                                    {msg.attachments && msg.attachments.length > 0 && (
                                        <div className={`flex flex-wrap gap-2 mb-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                            {msg.attachments.map((att, i) => (
                                                <div key={i} className="p-2 rounded-xl dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 flex items-center gap-2 max-w-[200px]">
                                                    <FileText size={14} className="text-slate-500" />
                                                    <span className="text-xs truncate text-slate-700 dark:text-slate-300">
                                                        {att.filename || att.name || 'File'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Message Bubble */}
                                    {(msg.content || msg.text) && (
                                        <div className={`px-4 py-2 text-sm shadow-sm leading-relaxed ${isMe
                                            ? 'bg-yellow-600 text-white rounded-2xl rounded-tr-sm'
                                            : 'dark:bg-slate-800 bg-slate-100 dark:text-slate-200 text-slate-800 border dark:border-slate-700/50 border-slate-200 rounded-2xl rounded-tl-sm'
                                            }`}>
                                            {msg.content || msg.text}
                                        </div>
                                    )}
                                    <span className="text-[10px] text-slate-400 px-1 opacity-50">
                                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 dark:bg-slate-900 bg-white border-t dark:border-slate-800 border-slate-200 z-30">
                {chat.attachments && chat.attachments.length > 0 && (
                    <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
                        {chat.attachments.map((att, i) => (
                            <div key={i} className="relative group flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border dark:border-slate-700 border-slate-200">
                                <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                    <FileText size={16} className="text-slate-400" />
                                </div>
                                <button
                                    onClick={() => onRemoveAttachment(i)}
                                    className="absolute top-0 right-0 bg-black/50 text-white p-0.5 rounded-bl hover:bg-red-500 transition-colors"
                                >
                                    <X size={10} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <div className="flex gap-1 pb-2">
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            className="hidden"
                            onChange={onFileSelect}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 rounded-full text-yellow-500 hover:bg-yellow-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            <Paperclip size={20} />
                        </button>
                        <div className="relative">
                            <button
                                onClick={onToggleEmojiPicker}
                                className="p-2 rounded-full text-yellow-500 hover:bg-yellow-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                <Smile size={20} />
                            </button>
                            {chat.showEmojiPicker && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={onToggleEmojiPicker}></div>
                                    <div className="absolute bottom-12 left-0 z-50 shadow-2xl rounded-2xl border dark:border-slate-700 overflow-hidden">
                                        <Suspense fallback={<div className="w-64 h-64 bg-white dark:bg-slate-900" />}>
                                            <EmojiPicker onEmojiClick={onEmojiClick} width={300} height={350} />
                                        </Suspense>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-3xl flex items-center px-4 py-2 border dark:border-slate-700 focus-within:border-yellow-500 focus-within:ring-1 focus-within:ring-yellow-500 transition-all">
                        <input
                            value={chat.input || ''}
                            onChange={(e) => onInputChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    onSend();
                                }
                            }}
                            placeholder="Type a message..."
                            className="flex-1 bg-transparent border-none outline-none text-sm dark:text-white text-slate-900 placeholder-slate-500"
                        />
                    </div>
                    <button
                        onClick={onSend}
                        disabled={(!chat.input?.trim() && (!chat.attachments || chat.attachments.length === 0)) || chat.isSending}
                        className="p-3 mb-0.5 rounded-full bg-yellow-600 hover:bg-yellow-500 text-white disabled:opacity-50 transition-all shadow-md"
                    >
                        {chat.isSending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

const TeamChatWidget = ({ userEmail, initialConversations = [], onUnreadCountChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [conversations, setConversations] = useState(initialConversations);
    const [openChats, setOpenChats] = useState([]); // Array of open chat windows

    // Calculate total unread count
    const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

    // Notify parent of unread count changes
    useEffect(() => {
        if (onUnreadCountChange) {
            onUnreadCountChange(totalUnread);
        }
    }, [totalUnread, onUnreadCountChange]);

    // Update conversations if props change
    useEffect(() => {
        if (initialConversations && initialConversations.length > 0) {
            // Normalize initial conversations and sort by most recent first
            const normalized = initialConversations.map(conv => ({
                ...conv,
                last_message_preview: conv.last_message_preview || conv.last_message?.text || conv.last_message?.content || '',
                last_message_at: conv.last_message_at || conv.updated_at || conv.last_message?.created_at
            })).sort((a, b) => {
                // Sort by last_message_at timestamp (most recent first)
                const dateA = new Date(a.last_message_at || 0);
                const dateB = new Date(b.last_message_at || 0);
                return dateB - dateA;
            });
            setConversations(normalized);
        }
    }, [initialConversations]);

    // Load conversations on mount or when opening
    useEffect(() => {
        if (isOpen) {
            loadConversations();
        }
    }, [isOpen]);

    const loadConversations = async () => {
        try {
            const res = await ChatService.getConversations();
            if (res.data) {
                // Normalize conversation fields and sort by most recent first
                const normalized = res.data.map(conv => ({
                    ...conv,
                    last_message_preview: conv.last_message_preview || conv.last_message?.text || conv.last_message?.content || '',
                    last_message_at: conv.last_message_at || conv.updated_at || conv.last_message?.created_at
                })).sort((a, b) => {
                    // Sort by last_message_at timestamp (most recent first)
                    const dateA = new Date(a.last_message_at || 0);
                    const dateB = new Date(b.last_message_at || 0);
                    return dateB - dateA;
                });
                setConversations(normalized);
            }
        } catch (err) {
            console.error("Failed to load widget conversations", err);
        }
    };

    const handleConversationSelect = (conv) => {
        // Check if chat is already open
        const existingChat = openChats.find(chat => chat.id === conv.id);
        if (existingChat) {
            // Focus on existing chat (bring to front)
            setOpenChats(prev => [
                ...prev.filter(c => c.id !== conv.id),
                existingChat
            ]);
            return;
        }

        // Open new chat window
        setOpenChats(prev => [...prev, {
            id: conv.id,
            conversation: conv,
            messages: [],
            input: '',
            attachments: [],
            isSending: false,
            isLoading: true
        }]);
    };

    const handleCloseChat = (convId) => {
        setOpenChats(prev => prev.filter(chat => chat.id !== convId));
    };

    const updateChatState = (convId, updates) => {
        setOpenChats(prev => prev.map(chat =>
            chat.id === convId ? { ...chat, ...updates } : chat
        ));
    };

    // Load messages for newly opened chats
    useEffect(() => {
        openChats.forEach(chat => {
            if (chat.isLoading) {
                loadMessagesForChat(chat.id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openChats.map(c => c.id + c.isLoading).join()]);

    const loadMessagesForChat = async (convId) => {
        try {
            const res = await ChatService.getMessages(convId);
            if (res.data) {
                // Normalize message fields and sort chronologically (oldest first)
                const normalized = res.data.map(msg => ({
                    ...msg,
                    content: msg.content || msg.text || msg.message || '',
                    text: msg.text || msg.content || msg.message || ''
                })).sort((a, b) => {
                    // Sort by created_at timestamp (oldest first)
                    const dateA = new Date(a.created_at || 0);
                    const dateB = new Date(b.created_at || 0);
                    return dateA - dateB;
                });
                updateChatState(convId, { messages: normalized, isLoading: false });
            }
        } catch (err) {
            console.error("Failed to load messages for chat", convId, err);
            updateChatState(convId, { isLoading: false });
        }
    };

    const handleSendMessage = async (convId) => {
        const chat = openChats.find(c => c.id === convId);
        if (!chat) return;

        const messageText = (chat.input || '').trim();

        // Require either text content or attachments
        if (!messageText && chat.attachments.length === 0) return;
        const attachmentIds = chat.attachments.map(att => att.id);

        // Optimistic update
        const optimisticMsg = {
            id: `temp-${Date.now()}`,
            text: messageText,
            content: messageText,
            sender_email: userEmail,
            created_at: new Date().toISOString(),
            attachments: chat.attachments.map(att => ({
                ...att,
                id: att.id,
                filename: att.filename || att.name,
                file_type: att.file_type || att.type,
                signed_url: att.signed_url || att.url
            })),
            link_previews: []
        };

        updateChatState(convId, {
            messages: [...chat.messages, optimisticMsg],
            input: '',
            attachments: [],
            isSending: true
        });

        try {
            const res = await ChatService.sendMessage(convId, messageText, attachmentIds, []);
            if (res.data) {
                // Merge optimistic attachments if backend dropped them
                const finalMsg = {
                    ...res.data,
                    attachments: res.data.attachments?.length > 0 ? res.data.attachments : optimisticMsg.attachments
                };

                // Replace optimistic message with real one and sort
                const updatedMessages = chat.messages.map(m => m.id === optimisticMsg.id ? finalMsg : m);
                updatedMessages.sort((a, b) => {
                    const dateA = new Date(a.created_at || 0);
                    const dateB = new Date(b.created_at || 0);
                    return dateA - dateB;
                });

                updateChatState(convId, {
                    messages: updatedMessages,
                    isSending: false
                });

                // Update conversation list to show latest message at top
                setConversations(prev => {
                    const updated = prev.map(conv => {
                        if (conv.id === convId) {
                            return {
                                ...conv,
                                last_message_preview: messageText,
                                last_message_at: new Date().toISOString()
                            };
                        }
                        return conv;
                    }).sort((a, b) => {
                        const dateA = new Date(a.last_message_at || 0);
                        const dateB = new Date(b.last_message_at || 0);
                        return dateB - dateA;
                    });
                    return updated;
                });
            }
        } catch (err) {
            console.error("Failed to send message", err);
            // Remove optimistic message on error
            updateChatState(convId, {
                messages: chat.messages.filter(m => m.id !== optimisticMsg.id),
                isSending: false
            });
        }
    };

    const handleFileSelect = async (convId, e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const chat = openChats.find(c => c.id === convId);
        if (!chat) return;

        for (const file of files) {
            try {
                const res = await ChatService.uploadAttachment(file);
                if (res.data) {
                    updateChatState(convId, {
                        attachments: [...chat.attachments, {
                            id: res.data.id,
                            filename: res.data.filename,
                            name: res.data.filename,
                            file_type: res.data.file_type,
                            type: res.data.file_type,
                            signed_url: res.data.signed_url,
                            url: res.data.signed_url
                        }]
                    });
                }
            } catch (err) {
                console.error("Failed to upload attachment", err);
            }
        }
    };

    const removeAttachment = (convId, index) => {
        const chat = openChats.find(c => c.id === convId);
        if (!chat) return;
        updateChatState(convId, {
            attachments: chat.attachments.filter((_, i) => i !== index)
        });
    };

    const handleEmojiClick = (convId, emojiData) => {
        const chat = openChats.find(c => c.id === convId);
        if (!chat) return;

        // emoji-picker-react v4.x returns the emoji in emojiData.emoji
        const emoji = emojiData?.emoji || emojiData?.native || emojiData;
        const currentInput = chat.input || '';

        updateChatState(convId, {
            input: currentInput + emoji,
            showEmojiPicker: false
        });
    };

    const getOtherParticipantName = (conv) => {
        if (!conv) return 'Unknown';
        if (conv.name) return conv.name;

        if (conv.participants && conv.participants.length > 0) {
            // If it's a group chat (more than 2 people including self), and no name, list names
            if (conv.participants.length > 2) {
                return conv.participants.map(p => p.name || p.email.split('@')[0]).join(', ');
            }

            // Direct message: find the other person
            const other = conv.participants.find(p => p.email !== userEmail);
            if (other) {
                return other.name || other.email || 'Unknown';
            }

            // Fallback if we can't find "other" (e.g. self chat?)
            if (conv.participants[0]) {
                return conv.participants[0].name || conv.participants[0].email || 'Unknown';
            }
        }
        return 'Unknown Chat';
    };

    return (
        <div className="fixed bottom-6 right-6 z-[100] font-sans">
            <AnimatePresence>
                {/* Conversation List Bubble */}
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl shadow-2xl w-[380px] h-[600px] flex flex-col overflow-hidden backdrop-blur-xl mb-4"
                    >
                        {/* Header */}
                        <div className="p-3 px-4 border-b dark:border-slate-800 border-slate-200 bg-gradient-to-r dark:from-indigo-900/20 dark:to-yellow-900/20 from-indigo-50 to-yellow-50 backdrop-blur-md flex justify-between items-center shadow-sm z-20">
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-yellow-500 flex items-center justify-center shadow-md">
                                        <Users className="text-white" size={20} />
                                    </div>
                                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 dark:border-slate-900 border-white rounded-full"></span>
                                </div>
                                <div>
                                    <h3 className="font-bold dark:text-white text-slate-900 leading-snug truncate max-w-[180px]">
                                        Team Chat
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Active now
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-slate-400 hover:text-red-500 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Conversation List */}
                        <div className="flex-1 overflow-hidden relative bg-white dark:bg-slate-950">
                            <div className="h-full overflow-y-auto custom-scrollbar p-2 space-y-1">
                                <div className="px-2 py-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                                        <input
                                            placeholder="Search messages..."
                                            className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                        />
                                    </div>
                                </div>

                                {conversations.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                                        <MessageSquare size={32} className="mb-2 opacity-50" />
                                        <p className="text-sm">No conversations yet.</p>
                                    </div>
                                ) : (
                                    conversations.map(conv => (
                                        <button
                                            key={conv.id}
                                            onClick={() => handleConversationSelect(conv)}
                                            className={`w-full p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 flex items-center gap-3 transition-colors text-left group ${conv.unread_count > 0 ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}`}
                                        >
                                            <div className="relative">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-semibold transition-colors ${conv.unread_count > 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 group-hover:bg-yellow-100 dark:group-hover:bg-yellow-900/30 group-hover:text-yellow-600'}`}>
                                                    {getOtherParticipantName(conv).charAt(0) || '?'}
                                                </div>
                                                {conv.unread_count > 0 && (
                                                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                                                        {conv.unread_count > 99 ? '99+' : conv.unread_count}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-baseline mb-0.5">
                                                    <span className={`truncate ${conv.unread_count > 0 ? 'font-bold text-slate-900 dark:text-white' : 'font-semibold text-slate-900 dark:text-slate-200'}`}>{getOtherParticipantName(conv) || 'Unknown'}</span>
                                                    <span className={`text-[10px] ${conv.unread_count > 0 ? 'text-yellow-500 font-medium' : 'text-slate-400'}`}>{new Date(conv.last_message_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                                <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-slate-700 dark:text-slate-300 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                                                    {conv.last_message_preview || "Start a conversation"}
                                                </p>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Individual Chat Windows */}
                {openChats.map((chat, index) => (
                    <ChatWindow
                        key={chat.id}
                        chat={chat}
                        index={index}
                        userEmail={userEmail}
                        onClose={() => handleCloseChat(chat.id)}
                        onSend={() => handleSendMessage(chat.id)}
                        onInputChange={(value) => updateChatState(chat.id, { input: value })}
                        onFileSelect={(e) => handleFileSelect(chat.id, e)}
                        onRemoveAttachment={(idx) => removeAttachment(chat.id, idx)}
                        onEmojiClick={(emojiData) => handleEmojiClick(chat.id, emojiData)}
                        onToggleEmojiPicker={() => updateChatState(chat.id, { showEmojiPicker: !chat.showEmojiPicker })}
                        getOtherParticipantName={getOtherParticipantName}
                    />
                ))}
            </AnimatePresence>

            {/* Existing Trigger Button Style */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 relative group ${isOpen ? 'bg-slate-800 text-slate-300 rotate-90' : 'bg-gradient-to-br from-indigo-600 to-yellow-600 text-white hover:shadow-yellow-500/25 ring-4 ring-white dark:ring-slate-900'
                    }`}
            >
                {isOpen ? <X size={24} /> : (
                    <div className="relative">
                        <MessageSquare size={24} />
                        {totalUnread > 0 && (
                            <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 bg-red-500 border-2 border-white dark:border-slate-900 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                                {totalUnread > 99 ? '99+' : totalUnread}
                            </span>
                        )}
                    </div>
                )}
            </motion.button>
        </div>
    );
};

export default TeamChatWidget;
